import { get } from 'svelte/store';
import { format } from 'svelte-i18n';

import { convert } from './utilities';
import {
	get_z_wave_channel_structure_from_category,
	get_z_wave_component_from_category,
	get_z_wave_ison,
	getZWaveChannelIndex,
} from './interface_helpers';
import type { Device } from 'src/types/Device';

type StatusLabel = {
	name: string;
	icon: string;
	value: string;
};

const translate = (key: string, defaultText?: string) =>
	get(format)(key, defaultText ? { default: defaultText } : undefined);

export const resolveZWaveComponentKey = (
	device: Device,
	status: Record<string, unknown> | undefined,
): string => {
	const defaultKey = get_z_wave_channel_structure_from_category(device);
	if (status?.[defaultKey]) return defaultKey;

	const prefix = get_z_wave_component_from_category(device);
	const channelIndex = getZWaveChannelIndex(device);
	const xcomps = Array.isArray(status?.xcomps) ? (status.xcomps as string[]) : [];

	const matchedByChannel = xcomps.find((key) => {
		if (!key.startsWith(prefix)) return false;
		const channelName = (status?.[key] as Record<string, unknown>)?._chnm;
		return typeof channelName === 'number' && channelName === channelIndex;
	});
	if (matchedByChannel) return matchedByChannel;

	const firstMatch = xcomps.find((key) => key.startsWith(prefix));
	return firstMatch ?? defaultKey;
};

const formatCoverState = (state: string): string => {
	const key = state === 'open' ? 'state_open_copy' : `state_${state}`;
	return translate(key, state);
};

const getCoverStateIcon = (state: string, position?: number | null): string => {
	if (typeof position === 'number') {
		return position > 0 ? 'fa fa-blinds-raised' : 'fa fa-blinds';
	}
	if (state === 'open' || state === 'opening') return 'fa fa-blinds-raised';
	return 'fa fa-blinds';
};

export const buildZWaveCoverStatusLabels = (
	device: Device,
	status: Record<string, unknown> | undefined,
): StatusLabel[] => {
	const componentKey = resolveZWaveComponentKey(device, status);
	const coverStatus = status?.[componentKey] as Record<string, unknown> | undefined;
	if (!coverStatus) return [];

	const position = coverStatus.current_pos ?? coverStatus.currentValue ?? coverStatus.pos;
	const hasPosition = typeof position !== 'undefined' && position !== null;

	if (hasPosition) {
		return [
			{
				name: 'position',
				icon: getCoverStateIcon('', position as number),
				value: `${position} %`,
			},
		];
	}

	if (typeof coverStatus.state !== 'string') return [];

	return [
		{
			name: 'position',
			icon: getCoverStateIcon(coverStatus.state),
			value: formatCoverState(coverStatus.state),
		},
	];
};

export const buildZWaveSwitchStatusLabels = (
	device: Device,
	status: Record<string, unknown> | undefined,
): StatusLabel[] => {
	const componentKey = resolveZWaveComponentKey(device, status);
	const switchStatus = status?.[componentKey] as Record<string, unknown> | undefined;
	if (!switchStatus) return [];

	const isOn = get_z_wave_ison(switchStatus);
	const labels: StatusLabel[] = [];

	if (typeof switchStatus.apower !== 'undefined') {
		labels.push({
			name: 'power',
			icon: 'fas fa-bolt',
			value: isOn ? convert(switchStatus.apower as number, 'W') : `${0} W`,
		});
	}

	return labels;
};

export const buildZWaveDimmerStatusLabels = (
	device: Device,
	status: Record<string, unknown> | undefined,
): StatusLabel[] => {
	const componentKey = resolveZWaveComponentKey(device, status);
	const lightStatus = status?.[componentKey] as Record<string, unknown> | undefined;
	if (!lightStatus) return [];

	const level = lightStatus.brightness ?? lightStatus.currentValue ?? lightStatus.value;
	if (typeof level === 'undefined' || level === null) return [];

	return [
		{
			name: 'brightness',
			icon: 'fas fa-lightbulb-on',
			value: `${level} %`,
		},
	];
};

const ZWAVE_ACTUATOR_PREFIXES = ['switch:', 'light:', 'cover:'] as const;
const ZWAVE_INPUT_PREFIXES = ['input:', 'v_eve:'] as const;

/** Collect status / xcomps keys for a component prefix (`input:`, `temperature:`, …). */
export const listZWaveStatusKeysByPrefix = (
	status: Record<string, unknown> | undefined,
	prefix: string,
): string[] => {
	if (!status) return [];
	const fromStatus = Object.keys(status).filter((key) => key.startsWith(prefix));
	const xcomps = Array.isArray(status.xcomps) ? (status.xcomps as string[]) : [];
	const fromXcomps = xcomps.filter((key) => key.startsWith(prefix));
	return [...new Set([...fromStatus, ...fromXcomps])].sort((a, b) => {
		const idA = Number(a.split(':')[1]);
		const idB = Number(b.split(':')[1]);
		return (Number.isFinite(idA) ? idA : 0) - (Number.isFinite(idB) ? idB : 0);
	});
};

export const deviceHasZWaveInputs = (status: Record<string, unknown> | undefined): boolean =>
	ZWAVE_INPUT_PREFIXES.some((prefix) => listZWaveStatusKeysByPrefix(status, prefix).length > 0);

export const deviceHasZWaveActuator = (status: Record<string, unknown> | undefined): boolean =>
	ZWAVE_ACTUATOR_PREFIXES.some((prefix) => listZWaveStatusKeysByPrefix(status, prefix).length > 0);

export const countZWaveInputChannels = (status: Record<string, unknown> | undefined): number => {
	const inputKeys = listZWaveStatusKeysByPrefix(status, 'input:');
	if (inputKeys.length > 0) return inputKeys.length;
	return listZWaveStatusKeysByPrefix(status, 'v_eve:').length;
};

/**
 * Map Wave Input NotifyEvent names to Gen2 inputs_reader codes used by the visualizer.
 */
export const mapZWaveInputEventToReaderCode = (event: string | undefined): string => {
	if (!event) return '';
	const normalized = event.trim().toLowerCase();
	switch (normalized) {
		case 'single_push':
		case 's':
			return 'S';
		case 'double_push':
		case 'ss':
			return 'SS';
		case 'triple_push':
		case 'sss':
			return 'SSS';
		case 'long_push':
		case 'l':
			return 'L';
		case 'sl':
			return 'SL';
		case 'ls':
			return 'LS';
		default:
			return event;
	}
};

const firstComponentPayload = (
	status: Record<string, unknown> | undefined,
	prefixes: string[],
): Record<string, unknown> | undefined => {
	for (const prefix of prefixes) {
		const key = listZWaveStatusKeysByPrefix(status, prefix)[0];
		if (!key) continue;
		const payload = status?.[key];
		if (payload && typeof payload === 'object') {
			return payload as Record<string, unknown>;
		}
	}
	return undefined;
};

const getBatteryPercentIcon = (percent: number): string => {
	if (percent <= 10) return 'fas fa-battery-slash';
	if (percent <= 25) return 'fas fa-battery-quarter';
	if (percent <= 50) return 'fas fa-battery-half';
	if (percent <= 75) return 'fas fa-battery-three-quarters';
	return 'fas fa-battery-full';
};

/** Status labels for Wave sensor / input devices (no actuator). */
export const buildZWaveSensorStatusLabels = (
	_device: Device,
	status: Record<string, unknown> | undefined,
): StatusLabel[] => {
	if (!status) return [];

	const labels: StatusLabel[] = [];

	const temperature = firstComponentPayload(status, ['temperature:']);
	if (temperature) {
		const tC = temperature.tC;
		const tF = temperature.tF;
		if (typeof tC === 'number') {
			labels.push({
				name: 'temperature',
				icon: 'fas fa-thermometer-half',
				value: `${Math.round(tC * 10) / 10} °C`,
			});
		} else if (typeof tF === 'number') {
			labels.push({
				name: 'temperature',
				icon: 'fas fa-thermometer-half',
				value: `${Math.round(tF * 10) / 10} °F`,
			});
		}
	}

	const humidity = firstComponentPayload(status, ['humidity:']);
	if (typeof humidity?.rh === 'number') {
		labels.push({
			name: 'humidity',
			icon: 'fas fa-humidity',
			value: `${humidity.rh} %`,
		});
	}

	const illuminance = firstComponentPayload(status, ['illuminance:']);
	if (illuminance) {
		const lux = illuminance.lux ?? illuminance.illumination;
		if (typeof lux === 'number') {
			labels.push({
				name: 'lux',
				icon: 'fas fa-sun',
				value: `${lux} lx`,
			});
		}
	}

	const door =
		firstComponentPayload(status, ['window:']) || firstComponentPayload(status, ['doorwindow:']);
	if (door) {
		const isOpen =
			typeof door.open === 'boolean'
				? door.open
				: typeof door.value === 'boolean'
					? door.value
					: undefined;
		if (typeof isOpen === 'boolean') {
			labels.push({
				name: 'door',
				icon: isOpen ? 'fas fa-door-open' : 'fas fa-door-closed',
				value: translate(isOpen ? 'state_opened' : 'state_closed', isOpen ? 'Opened' : 'Closed'),
			});
		}
	}

	const motion = firstComponentPayload(status, ['motion:']);
	if (motion) {
		const detected =
			typeof motion.motion === 'boolean'
				? motion.motion
				: typeof motion.value === 'boolean'
					? motion.value
					: undefined;
		if (typeof detected === 'boolean') {
			labels.push({
				name: 'motion',
				icon: 'fas fa-walking',
				value: translate(
					detected ? 'presence_event_motion_detected' : 'status_no_motion',
					detected ? 'Motion Detected' : 'No motion',
				),
			});
		}
	}

	const devicePower = firstComponentPayload(status, ['devicepower:']);
	if (devicePower) {
		const battery = devicePower.battery as Record<string, unknown> | undefined;
		const external = devicePower.external as Record<string, unknown> | undefined;
		if (external?.present === true) {
			labels.push({
				name: 'battery',
				icon: 'fas fa-plug',
				value: translate('sub_settings_title_external_power', 'External power'),
			});
		} else if (typeof battery?.percent === 'number') {
			labels.push({
				name: 'battery',
				icon: getBatteryPercentIcon(battery.percent),
				value: `${battery.percent} %`,
			});
		}
	}

	const pm1 = firstComponentPayload(status, ['pm1:']);
	if (typeof pm1?.apower === 'number') {
		labels.push({
			name: 'power',
			icon: 'fas fa-bolt',
			value: convert(pm1.apower, 'W'),
		});
	}

	const rotation = firstComponentPayload(status, ['rotation:']);
	if (typeof rotation?.value === 'number') {
		labels.push({
			name: 'tilt',
			icon: 'fas fa-angle',
			value: `${rotation.value}°`,
		});
	}

	return labels;
};

/** True when this Wave device should show On/Off / dimmer controls (not I4 / sensors). */
export const isZWaveActuatorDevice = (
	device: Device,
	status?: Record<string, unknown> | undefined,
): boolean => {
	if (device?.type === 'ZW-INPUT') return false;
	if (device?.category === 'inputs_reader' || device?.category === 'sensor') return false;
	if (device?.type === 'ZW-SENSOR' && !deviceHasZWaveActuator(status)) return false;
	if (device?.type === 'ZW-SWITCH' || device?.type === 'ZW-DIMMER' || device?.type === 'ZW-COVER') {
		return true;
	}
	return deviceHasZWaveActuator(status);
};
